import { useMemo, useState, useEffect, useRef } from "react";
import clsx from "clsx";
import type { CalendarEvent } from "../../api";
import {
  monthGridDays,
  dateKey,
  isToday,
  isSameMonth,
  isoAtPtHourOnDay,
  PT_TZ,
} from "../../lib/calendar-util";

interface Props {
  anchor: Date;
  events: CalendarEvent[];
  onEditEvent: (event: CalendarEvent) => void;
  onCreateAt: (startISO: string, allDay: boolean) => void;
}

const MAX_VISIBLE = 3;

/**
 * 6-row × 7-col month grid. Each cell shows the day number plus up to 3 event
 * chips, sorted (all-day first, then by start). Overflow surfaces as "+N more"
 * which opens a popover with the full day's list.
 */
export function CalendarMonthView({
  anchor,
  events,
  onEditEvent,
  onCreateAt,
}: Props) {
  const days = useMemo(() => monthGridDays(anchor), [anchor]);

  // Group events by date key. Each event may appear in multiple cells if it
  // spans days; week view handles cross-midnight via chunking, but in month
  // we keep it simple — list the event on every PT day it touches.
  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const evt of events) {
      const startKey = isoToPTDateKey(evt.startAt);
      const endKey = isoToPTDateKey(
        // For all-day: end is exclusive midnight; subtract 1ms to get inclusive day.
        new Date(new Date(evt.endAt).getTime() - 1).toISOString(),
      );
      // Iterate days from start to end (inclusive).
      let cursor = startKey;
      const upper = endKey;
      let safetyCounter = 0;
      while (cursor <= upper && safetyCounter < 100) {
        const list = map.get(cursor) ?? [];
        list.push(evt);
        map.set(cursor, list);
        cursor = nextDayKey(cursor);
        safetyCounter++;
      }
    }
    // Sort each cell's events: all-day first, then by start.
    for (const [k, v] of map) {
      v.sort((a, b) => {
        if (a.allDay !== b.allDay) return a.allDay ? -1 : 1;
        return a.startAt.localeCompare(b.startAt);
      });
      map.set(k, v);
    }
    return map;
  }, [events]);

  // Popover state for "+N more". Anchored to the cell that triggered.
  const [popoverDay, setPopoverDay] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  // Close popover on outside click.
  useEffect(() => {
    if (!popoverDay) return;
    const onDocClick = (e: MouseEvent) => {
      if (!popoverRef.current?.contains(e.target as Node)) {
        setPopoverDay(null);
      }
    };
    // Wait a tick so the click that opened the popover doesn't immediately close it.
    const id = window.setTimeout(() => {
      document.addEventListener("mousedown", onDocClick);
    }, 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener("mousedown", onDocClick);
    };
  }, [popoverDay]);

  const weekdayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div className="calendar-month">
      <div className="calendar-month-weekdays">
        {weekdayLabels.map((w) => (
          <div key={w} className="calendar-month-weekday">
            {w}
          </div>
        ))}
      </div>
      <div className="calendar-month-grid">
        {days.map((day) => {
          const key = dateKey(day);
          const inMonth = isSameMonth(day, anchor);
          const today = isToday(day);
          const dayEvents = eventsByDay.get(key) ?? [];
          const visible = dayEvents.slice(0, MAX_VISIBLE);
          const overflow = dayEvents.length - visible.length;
          return (
            <div
              key={key}
              className={clsx(
                "calendar-month-cell",
                !inMonth && "out-of-month",
                today && "today",
              )}
              onClick={(e) => {
                if (e.target !== e.currentTarget) return;
                onCreateAt(isoAtPtHourOnDay(key, 10), false);
              }}
            >
              <div className="calendar-month-cell-head">
                <span className="calendar-month-daynum">{dayNum(day)}</span>
              </div>
              <div className="calendar-month-cell-events">
                {visible.map((evt) => (
                  <div
                    key={`${key}-${evt.id}`}
                    className={clsx(
                      "calendar-month-chip",
                      `priority-${evt.priority}`,
                      evt.allDay ? "allday" : "timed",
                    )}
                    title={`${evt.title}${evt.dealName ? ` · ${evt.dealName.toUpperCase()}` : ""}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditEvent(evt);
                    }}
                  >
                    {!evt.allDay && <span className="calendar-month-chip-dot" />}
                    <span className="calendar-month-chip-time">
                      {evt.allDay ? "" : formatHourMin(evt.startAt) + " "}
                    </span>
                    <span className="calendar-month-chip-title">{evt.title}</span>
                  </div>
                ))}
                {overflow > 0 && (
                  <button
                    className="calendar-month-overflow"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPopoverDay(key);
                    }}
                  >
                    + {overflow} more
                  </button>
                )}
              </div>
              {popoverDay === key && (
                <div className="calendar-month-popover" ref={popoverRef}>
                  <div className="calendar-month-popover-head">
                    {longDate(day)}
                  </div>
                  {dayEvents.map((evt) => (
                    <button
                      key={evt.id}
                      className={clsx(
                        "calendar-month-popover-item",
                        `priority-${evt.priority}`,
                      )}
                      onClick={() => {
                        setPopoverDay(null);
                        onEditEvent(evt);
                      }}
                    >
                      <span className="calendar-month-chip-dot" />
                      <span className="calendar-month-chip-time">
                        {evt.allDay ? "ALL DAY · " : formatHourMin(evt.startAt) + " · "}
                      </span>
                      <span>{evt.title}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function nextDayKey(key: string): string {
  const [y, m, d] = key.split("-").map(Number);
  const next = new Date(Date.UTC(y, m - 1, d + 1, 20, 0, 0));
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: PT_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(next);
  const map: Record<string, string> = {};
  for (const p of parts) if (p.type !== "literal") map[p.type] = p.value;
  return `${map.year}-${map.month}-${map.day}`;
}

function isoToPTDateKey(iso: string): string {
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: PT_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const map: Record<string, string> = {};
  for (const p of parts) if (p.type !== "literal") map[p.type] = p.value;
  return `${map.year}-${map.month}-${map.day}`;
}

function dayNum(d: Date): string {
  return d.toLocaleDateString("en-US", { day: "numeric", timeZone: PT_TZ });
}
function longDate(d: Date): string {
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
    timeZone: PT_TZ,
  });
}
function formatHourMin(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: PT_TZ,
  });
}
